import React, {
 useEffect, useState, useRef, Fragment
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import SnmpUserForm from 'routes/SnmpUser/components/SnmpUserForm';
import * as snmpUserActions from 'data/snmp/snmp.actions';
import * as snmpUserSelectors from 'data/snmp/snmp.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { snmpUserTemplate } from 'routes/SnmpUser/templates/snmpUserTemplate';
import { adaptTemplateVisibilityMode, adaptSnmpUserTemplate } from 'routes/SnmpUser/utils/snmpUserTemplate.adapter';
import { SnmpV3User } from 'data/snmp/snmp.types';
import { getAuditDetailsToSubmit } from 'routes/SnmpUser/utils/snmpUserTemplateDataToSubmit';
import { FieldUpdateHandlerParam } from 'shared/form';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => SnmpV3User;
};

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  snmpUserTemplateDef: adaptTemplateVisibilityMode(snmpUserTemplate, 'edit'),
  selectedSnmpUser: snmpUserSelectors.getSelectedSnmpUser(state),
  snmpUser: snmpUserSelectors.getSnmpUser(state)
});

const mapDispatch = {
  goToSmnpUserList: snmpUserActions.goToSmnpUserList,
  updateSnmpUser: snmpUserActions.updateSnmpUser,
  createSnmpUser: snmpUserActions.createSnmpUser,
  getSnmpUser: snmpUserActions.getSnmpUser
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const SnmpUserEdit = ({
  history,
  userId,
  userName,
  isAuditEnabled,
  snmpUserTemplateDef,
  deliveryFunctionId,
  goToSmnpUserList,
  updateSnmpUser,
  getSnmpUser,
  snmpUser,
  selectedSnmpUser
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SnmpV3User>>();
  const [template, setTemplate] = useState(snmpUserTemplateDef);
  const { t } = useTranslation();

  const id = selectedSnmpUser?.id;

  useEffect(() => {
    if (id) {
      getSnmpUser(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getSnmpUser]);

  useEffect(() => {
    if (snmpUser == null || snmpUserTemplateDef == null) {
      return;
    }

    const clonedSnmpUser = cloneDeep(snmpUser);
    const clonedTemplate = cloneDeep(snmpUserTemplateDef);
    clonedTemplate.formId = Date.now().toString();

    setTemplate(clonedTemplate);
    setFormData(clonedSnmpUser);
  }, [snmpUser, snmpUserTemplateDef]);

  const handleClickSave = () => {
    const payload = getSnmpUserPayload();
    if (payload && snmpUser !== null) {
      updateSnmpUser(deliveryFunctionId, payload, history);
    }
  };

  const getSnmpUserPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const snmpUserFormData = formRef.current.getValues();

    const payload = {
      data: snmpUserFormData,
      auditDetails: getAuditDetailsToSubmit(
        snmpUserFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'UPDATE'
      )
    };
    return payload;
  };

  const handleCancel = () => goToSmnpUserList(history);

  const handleFieldUpdate = ({
    name: fieldName,
    value
  }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }
    const clonedFormData = cloneDeep(formData as SnmpV3User);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value
    };

    clonedTemplate = adaptSnmpUserTemplate(
      clonedTemplate,
      fieldName,
      value
    );

    setFormData(newData);
    setTemplate(clonedTemplate);
  };
  const renderForm = () => {
    if (formData && template) {
      return (
        <SnmpUserForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (snmpUser == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="SnmpUserEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('snmpUser.update.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SnmpUserEdit));
